import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AuctionGrid } from "@/components/auction-grid";
import { CardGridSkeleton, ErrorState } from "@/components/data-state";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { fetchAllActive } from "@/lib/auctions";
import type { AuctionCardData } from "@/components/auction-card";
import {
  ACCESSORY_LANDINGS,
  canonicalUrl,
  findAccessoryLanding,
  type AccessoryLanding,
} from "@/lib/accessory-categories";
import { socialImageMeta } from "@/lib/seo";

export const Route = createFileRoute("/accessories/$slug")({
  loader: ({ params }) => {
    const landing = findAccessoryLanding(params.slug);
    if (!landing) throw notFound();
    return { landing };
  },
  head: ({ loaderData }) => {
    const l = loaderData?.landing;
    if (!l) {
      return {
        meta: [
          { title: "Accessories — BidNova Auctions" },
          { name: "robots", content: "noindex,follow" },
        ],
      };
    }
    const url = canonicalUrl(l.slug);
    return {
      meta: [
        { title: l.title },
        { name: "description", content: l.description },
        { name: "robots", content: "index,follow" },
        { property: "og:title", content: l.title },
        { property: "og:description", content: l.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...socialImageMeta(),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: l.title },
        { name: "twitter:description", content: l.description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
              { "@type": "ListItem", position: 2, name: "Auctions", item: "https://bidnovaauctions.uk/auctions" },
              { "@type": "ListItem", position: 3, name: "Accessories", item: "https://bidnovaauctions.uk/auctions?product_type=accessory" },
              { "@type": "ListItem", position: 4, name: l.label, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AccessoryLandingPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function matchesLanding(a: AuctionCardData, l: AccessoryLanding): boolean {
  const product = ((a as { product_type?: string | null }).product_type ?? "trading_card").toLowerCase();
  if (product !== "accessory") return false;
  const cat = ((a as { accessory_category?: string | null }).accessory_category ?? "").toLowerCase();
  if (cat && (l.dbValues as readonly string[]).includes(cat)) return true;
  // Fallback: for broader SEO categories, match by title keyword when the row
  // is tagged with a bucket value (e.g. "other") that we grouped in.
  const keywords = l.titleKeywords ?? [];
  if (!keywords.length) return false;
  const title = (a.title ?? "").toLowerCase();
  return keywords.some((k) => title.includes(k));
}

function AccessoryLandingPage() {
  const { landing } = Route.useLoaderData();
  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ["all-active"],
    queryFn: fetchAllActive,
    refetchInterval: 60000,
  });
  const matched = (data ?? []).filter((a) => matchesLanding(a, landing));

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      {/* Visible breadcrumbs */}
      <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1">
          <li><Link to="/" className="hover:text-foreground">Home</Link></li>
          <li aria-hidden="true">/</li>
          <li><Link to="/auctions" className="hover:text-foreground">Auctions</Link></li>
          <li aria-hidden="true">/</li>
          <li>
            <Link
              to="/auctions"
              search={{ product_type: "accessory" } as never}
              className="hover:text-foreground"
            >
              Accessories
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="text-foreground">{landing.label}</li>
        </ol>
      </nav>

      <header className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">Accessories</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">{landing.h1}</h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">{landing.intro}</p>
      </header>

      <section aria-labelledby="listings" className="mt-4">
        <h2 id="listings" className="sr-only">Live listings</h2>
        {isLoading ? (
          <CardGridSkeleton count={8} />
        ) : isError ? (
          <ErrorState
            description="We couldn't load these listings."
            onRetry={() => refetch()}
            retrying={isRefetching}
          />
        ) : matched.length > 0 ? (
          <AuctionGrid auctions={matched} />
        ) : (
          <div className="rounded-2xl border border-border/60 bg-card p-10 text-center">
            <p className="text-sm text-muted-foreground">
              No {landing.label.toLowerCase()} are live right now. New listings go up throughout the day —
              browse{" "}
              <Link to="/auctions" search={{ product_type: "accessory" } as never} className="text-gold hover:underline">
                all accessory listings
              </Link>{" "}
              or check back shortly.
            </p>
          </div>
        )}
      </section>

      <section aria-labelledby="related-accessories" className="mt-14">
        <h2 id="related-accessories" className="font-display text-2xl">Related accessory categories</h2>
        <nav aria-label="Accessory categories" className="mt-4 flex flex-wrap gap-2 text-sm">
          {ACCESSORY_LANDINGS.filter((c) => c.slug !== landing.slug).map((c) => (
            <Link
              key={c.slug}
              to="/accessories/$slug"
              params={{ slug: c.slug }}
              className="rounded-full border border-gold/30 bg-gold/5 px-3 py-1 hover:bg-gold/10"
            >
              {c.label}
            </Link>
          ))}
          <Link
            to="/auctions"
            search={{ product_type: "accessory" } as never}
            className="rounded-full border border-border px-3 py-1 hover:bg-muted"
          >
            All accessories
          </Link>
        </nav>
      </section>

      <section className="mt-14 grid gap-8 md:grid-cols-2">
        <div>
          <h2 className="font-display text-2xl">Why buy accessories on BidNova</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            BidNova is a UK-registered marketplace. Every accessory is sold by a verified seller with a public rating,
            priced in GBP, and paid via Stripe. Funds are held until you confirm your order arrives as described.
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc pl-5">
            <li>Stripe-secured payments with buyer protection.</li>
            <li>Verified UK sellers with public ratings and dispute resolution.</li>
            <li>Prices in GBP — no surprise conversion fees.</li>
          </ul>
        </div>
        <div>
          <h2 className="font-display text-2xl">Selling accessories?</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Approved sellers can list accessories alongside singles and sealed product from one dashboard.
            Grading fields don't apply to accessories — just add photos, condition and a starting price.
          </p>
          <div className="mt-4">
            <Link to="/sell" className="inline-block rounded-full border border-gold px-4 py-2 text-sm hover:bg-gold/10">
              List an accessory
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
